import React from 'react';
import { RadioWithLabel } from 'components';
import { Calendar } from 'react-date-range';
import { EXPIRY_PERIOD_OPTIONS } from '../constants';
import DateUtils from 'utils/dateUtils';

interface ExpiryDateSectionProps {
  selectedDate: Date | null;
  onDateChange: (date: Date | null) => void;
  showCustomPicker: boolean;
  onCustomPickerChange: (show: boolean) => void;
}

const ExpiryDateSection: React.FC<ExpiryDateSectionProps> = ({
  selectedDate,
  onDateChange,
  showCustomPicker,
  onCustomPickerChange
}) => {
  const today = DateUtils.getNow();

  const expiryOptions = [
    ...EXPIRY_PERIOD_OPTIONS.map(option => ({
      days: option.days,
      label: option.btnLabel
    })),
    { days: -1, label: 'Custom' }
  ];

  const getSelectedDays = (date: Date | null): number => {
    if (!date) return 0; // No expiry
    const todayStart = DateUtils.getStartOfDay(today);
    const expiryEnd = DateUtils.getEndOfDay(date);
    const diffDays = DateUtils.calculateDaysDifference(expiryEnd, todayStart);
    const safeDiffDays = Math.abs(diffDays);
    return expiryOptions.find(opt => opt.days === safeDiffDays)?.days ?? -1;
  };

  const formatExpiryDate = (days: number) => {
    if (days === -1) {
      if (getSelectedDays(selectedDate) === days && selectedDate) {
        return `( On ${DateUtils.getDateInFormat({ date: selectedDate, dateFormat: 'MMM d, yyyy' })} )`;
      }
      return '';
    }
    if (days === 0) {
      return '';
    }
    const expiryDate = DateUtils.getEndOfDay(DateUtils.addDaysToDate(today, days));
    return `( On ${DateUtils.getDateInFormat({ date: expiryDate, dateFormat: 'MMM d, yyyy' })} )`;
  };

  const formatLabel = (option: { days: number; label: string }) => {
    if (option.days === -1) {
      return `${option.label} ${formatExpiryDate(option.days)}`;
    }
    if (option.days === 0) {
      return option.label;
    }
    return `In ${option.label} ${formatExpiryDate(option.days)}`;
  };

  const handleOptionClick = (days: number) => {
    if (days === -1) {
      onCustomPickerChange(true);
    } else {
      onCustomPickerChange(false);
      if (days === 0) {
        onDateChange(null);
      } else {
        onDateChange(DateUtils.getEndOfDay(DateUtils.addDaysToDate(today, days)));
      }
    }
  };

  const handleDateSelect = value => {
    onDateChange(new Date(value));
    onCustomPickerChange(false);
  };

  if (showCustomPicker) {
    return (
      <div className="flex flex-col gap-4">
        <Calendar
          date={selectedDate || today}
          onChange={value => {
            handleDateSelect(value);
          }}
          minDate={new Date()}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {expiryOptions.map(option => (
        <div
          className={`border-b border-secondaryBtn pb-4 ${
            option.days === expiryOptions[expiryOptions.length - 1].days ? 'border-b-0' : ''
          }`}
          key={option.days}>
          <RadioWithLabel
            isSelected={getSelectedDays(selectedDate) === option.days}
            label={formatLabel(option)}
            onClick={() => handleOptionClick(option.days)}
            labelStyle={{ display: 'flex', gap: '8px', flex: 1 }}
            radioPosition="right"
          />
        </div>
      ))}
    </div>
  );
};

export default ExpiryDateSection;
